import React, { useState, useEffect } from 'react';
import { 
  Calendar, Plus, Edit2, Trash2, DollarSign, Users, Award, 
  Tag, Package, Check, X, Copy, Eye, ChevronDown, ChevronUp,
  Ticket, Star, Building2, Mic, Utensils, Gift, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';
import EventSponsorshipManager from './EventSponsorshipManager';

const RAZORPAY_MAX_MINOR_UNITS = {
  USD: 500000,
};

const AdminEventFees = () => {
  const [events, setEvents] = useState([]);
  const [sponsorshipTypes, setSponsorshipTypes] = useState([]);
  const [delegateFeeTypes, setDelegateFeeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  
  // Dialogs
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showSponsorshipDialog, setShowSponsorshipDialog] = useState(false);
  const [showDelegateDialog, setShowDelegateDialog] = useState(false);
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [showSponsorshipManager, setShowSponsorshipManager] = useState(false);
  const [showRegistrationsDialog, setShowRegistrationsDialog] = useState(false);
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [eventRegistrationsLoading, setEventRegistrationsLoading] = useState(false);
  const [selectedEventForRegistrations, setSelectedEventForRegistrations] = useState(null);
  const [completingPaymentId, setCompletingPaymentId] = useState(null);
  const [rejectingPaymentId, setRejectingPaymentId] = useState(null);
  const [creatingPaymentLinkId, setCreatingPaymentLinkId] = useState(null);

  // Editing states
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingSponsorshipType, setEditingSponsorshipType] = useState(null);
  const [editingDelegateType, setEditingDelegateType] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Forms
  const [eventForm, setEventForm] = useState({
    name: '',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    currency: 'USD',
    early_bird_deadline: '',
    early_bird_discount: 10,
    is_active: true,
    delegate_fee: 0,
    max_attendees: 100
  });
  
  const [sponsorshipForm, setSponsorshipForm] = useState({
    name: '',
    description: '',
    price: 0,
    max_quantity: 1,
    benefits: [],
    sort_order: 0,
    is_active: true
  });
  
  const [delegateForm, setDelegateForm] = useState({
    name: '',
    description: '',
    price: 0,
    early_bird_price: 0,
    member_price: 0,
    group_discount_threshold: 5,
    group_discount_percent: 10,
    includes: [],
    sort_order: 0,
    is_active: true
  });
  
  const [feeForm, setFeeForm] = useState({
    event_id: '',
    fee_type: 'sponsorship', // sponsorship or delegate
    type_id: '',
    custom_price: null,
    quantity_available: null
  });
  
  const [newBenefit, setNewBenefit] = useState('');
  const [newInclude, setNewInclude] = useState('');
  
  const { api, hasPermission } = useAdminAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventsRes, sponsorshipsRes, delegatesRes] = await Promise.all([
        api.get('/api/admin/event-fees/events'),
        api.get('/api/admin/event-fees/sponsorship-types'),
        api.get('/api/admin/event-fees/delegate-types')
      ]);
      setEvents(eventsRes.data.events || []);
      setSponsorshipTypes(sponsorshipsRes.data.types || []);
      setDelegateFeeTypes(delegatesRes.data.types || []);
    } catch (error) {
      toast.error('Failed to load event fees configuration');
    } finally {
      setLoading(false);
    }
  };

  // Event handlers
  const handleSaveEvent = async () => {
    if (!eventForm.name || !eventForm.start_date) {
      toast.error('Please fill in event name and start date');
      return;
    }
    try {
      if (editingEvent) {
        await api.put(`/api/admin/event-fees/events/${editingEvent.event_id}`, eventForm);
        toast.success('Event updated');
      } else {
        await api.post('/api/admin/event-fees/events', eventForm);
        toast.success('Event created');
      }
      setShowEventDialog(false);
      resetEventForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to save event'));
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await api.delete(`/api/admin/event-fees/events/${eventId}`);
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to delete event'));
    }
  };

  // Sponsorship type handlers
  const handleSaveSponsorshipType = async () => {
    if (!sponsorshipForm.name || !sponsorshipForm.price) {
      toast.error('Please fill in name and price');
      return;
    }
    try {
      if (editingSponsorshipType) {
        await api.put(`/api/admin/event-fees/sponsorship-types/${editingSponsorshipType.type_id}`, sponsorshipForm);
        toast.success('Sponsorship type updated');
      } else {
        await api.post('/api/admin/event-fees/sponsorship-types', sponsorshipForm);
        toast.success('Sponsorship type created');
      }
      setShowSponsorshipDialog(false);
      resetSponsorshipForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to save sponsorship type'));
    }
  };

  const handleDeleteSponsorshipType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this sponsorship type?')) return;
    try {
      await api.delete(`/api/admin/event-fees/sponsorship-types/${typeId}`);
      toast.success('Sponsorship type deleted');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to delete sponsorship type'));
    }
  };

  // Delegate fee type handlers
  const handleSaveDelegateType = async () => {
    if (!delegateForm.name || !delegateForm.price) {
      toast.error('Please fill in name and price');
      return;
    }
    try {
      if (editingDelegateType) {
        await api.put(`/api/admin/event-fees/delegate-types/${editingDelegateType.type_id}`, delegateForm);
        toast.success('Delegate fee type updated');
      } else {
        await api.post('/api/admin/event-fees/delegate-types', delegateForm);
        toast.success('Delegate fee type created');
      }
      setShowDelegateDialog(false);
      resetDelegateForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to save delegate type'));
    }
  };

  const handleDeleteDelegateType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this delegate fee type?')) return;
    try {
      await api.delete(`/api/admin/event-fees/delegate-types/${typeId}`);
      toast.success('Delegate fee type deleted');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to delete delegate type'));
    }
  };

  // Add fee to event
  const handleAddFeeToEvent = async () => {
    try {
      await api.post(`/api/admin/event-fees/events/${feeForm.event_id}/fees`, {
        fee_type: feeForm.fee_type,
        type_id: feeForm.type_id,
        custom_price: feeForm.custom_price,
        quantity_available: feeForm.quantity_available
      });
      toast.success('Fee added to event');
      setShowFeeDialog(false);
      resetFeeForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to add fee'));
    }
  };

  // Form reset functions
  const resetEventForm = () => {
    setEventForm({
      name: '',
      description: '',
      location: '',
      start_date: '',
      end_date: '',
      currency: 'USD',
      early_bird_deadline: '',
      early_bird_discount: 10,
      is_active: true,
      delegate_fee: 0,
      max_attendees: 100
    });
    setEditingEvent(null);
  };

  const resetSponsorshipForm = () => {
    setSponsorshipForm({
      name: '',
      description: '',
      price: 0,
      max_quantity: 1,
      benefits: [],
      sort_order: 0,
      is_active: true
    });
    setEditingSponsorshipType(null);
    setNewBenefit('');
  };

  const resetDelegateForm = () => {
    setDelegateForm({
      name: '',
      description: '',
      price: 0,
      early_bird_price: 0,
      member_price: 0,
      group_discount_threshold: 5,
      group_discount_percent: 10,
      includes: [],
      sort_order: 0,
      is_active: true
    });
    setEditingDelegateType(null);
    setNewInclude('');
  };

  const resetFeeForm = () => {
    setFeeForm({
      event_id: '',
      fee_type: 'sponsorship',
      type_id: '',
      custom_price: null,
      quantity_available: null
    });
  };

  // Helper functions
  const addBenefit = () => {
    if (!newBenefit.trim()) return;
    setSponsorshipForm({
      ...sponsorshipForm,
      benefits: [...sponsorshipForm.benefits, newBenefit.trim()]
    });
    setNewBenefit('');
  };

  const removeBenefit = (index) => {
    setSponsorshipForm({
      ...sponsorshipForm,
      benefits: sponsorshipForm.benefits.filter((_, i) => i !== index)
    });
  };

  const addInclude = () => {
    if (!newInclude.trim()) return;
    setDelegateForm({
      ...delegateForm,
      includes: [...delegateForm.includes, newInclude.trim()]
    });
    setNewInclude('');
  };

  const removeInclude = (index) => {
    setDelegateForm({
      ...delegateForm,
      includes: delegateForm.includes.filter((_, i) => i !== index)
    });
  };

  const fetchEventRegistrations = async (eventId) => {
    setEventRegistrationsLoading(true);
    try {
      const res = await api.get(`/api/admin/event-fees/events/${eventId}/registrations`);
      setEventRegistrations(res.data.registrations || []);
    } catch (e) {
      toast.error(getErrorMessage(e.response?.data?.detail, 'Failed to load registrations'));
      setEventRegistrations([]);
    } finally {
      setEventRegistrationsLoading(false);
    }
  };

  const handleRegistrationActionError = async (error, fallbackMessage, eventId) => {
    if (error?.response?.status === 404 && eventId) {
      await fetchEventRegistrations(eventId);
      toast.error('This registration no longer exists. The list has been refreshed.');
      return;
    }

    toast.error(getErrorMessage(error.response?.data?.detail, fallbackMessage));
  };

  const openRegistrationsDialog = (event) => {
    setSelectedEventForRegistrations(event);
    setEventRegistrations([]);
    setShowRegistrationsDialog(true);
    fetchEventRegistrations(event.event_id);
  };

  const handleCompletePayment = async (eventId, registrationId) => {
    setCompletingPaymentId(registrationId);
    try {
      await api.put(
        `/api/admin/event-fees/events/${eventId}/registrations/${registrationId}/complete-payment`
      );
      toast.success('Payment marked complete; member will be notified by email.');
      fetchEventRegistrations(eventId);
    } catch (e) {
      await handleRegistrationActionError(e, 'Failed to complete payment', eventId);
    } finally {
      setCompletingPaymentId(null);
    }
  };

  const getNormalizedPaymentMethod = (registration) =>
    String(registration?.payment_method || '').trim().toLowerCase();

  const isRazorpayRegistration = (registration) =>
    getNormalizedPaymentMethod(registration).includes('razorpay');

  const isBankTransferRegistration = (registration) => {
    const method = getNormalizedPaymentMethod(registration);
    return method.includes('bank transfer') || method.includes('bank_transfer');
  };

  const getPaymentProofUrl = (registration) => {
    const proofUrl = registration?.bank_transfer_proof;
    if (typeof proofUrl !== 'string') return '';
    return proofUrl;
  };

  const getRegistrationCurrency = (registration) =>
    String(
      registration?.currency ||
      selectedEventForRegistrations?.currency ||
      'USD'
    ).toUpperCase();

  const getRegistrationAmountMinorUnits = (registration) =>
    Math.round(Number(registration?.amount_paid || 0) * 100);

  const isRazorpayLinkAllowed = (registration) => {
    if (!isRazorpayRegistration(registration)) return false;
    const currency = getRegistrationCurrency(registration);
    const maxMinorUnits = RAZORPAY_MAX_MINOR_UNITS[currency];
    if (!maxMinorUnits) return true;
    return getRegistrationAmountMinorUnits(registration) <= maxMinorUnits;
  };

  const getRazorpayLinkIssue = (registration) => {
    if (!isRazorpayRegistration(registration)) return '';
    const currency = getRegistrationCurrency(registration);
    const maxMinorUnits = RAZORPAY_MAX_MINOR_UNITS[currency];
    if (!maxMinorUnits) {
      return registration?.payment_link_url
        ? 'Use the payment link to verify the checkout state.'
        : 'No payment link is saved for this registration yet. Use Add Payment Link to generate one.';
    }

    const amountMinorUnits = getRegistrationAmountMinorUnits(registration);
    if (amountMinorUnits > maxMinorUnits) {
      return `This registration totals ${currency} ${(amountMinorUnits / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}. Razorpay supports up to ${currency} ${(maxMinorUnits / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} per payment, so no Razorpay payment link can be created for this row. Ask the member to use Bank Transfer instead.`;
    }

    return registration?.payment_link_url
      ? 'Use the payment link to verify the checkout state.'
      : 'No payment link is saved for this registration yet. Use Add Payment Link to generate one.';
  };

  const handleRejectPayment = async (eventId, registrationId) => {
    const reason = window.prompt('Enter reason for rejecting this pending payment:');
    if (reason === null) return;

    setRejectingPaymentId(registrationId);
    try {
      await api.post(
        `/api/admin/event-fees/events/${eventId}/registrations/${registrationId}/reject-payment`,
        { reason: reason.trim() }
      );
      toast.success('Payment rejected and registration removed.');
      fetchEventRegistrations(eventId);
    } catch (e) {
      await handleRegistrationActionError(e, 'Failed to reject payment', eventId);
    } finally {
      setRejectingPaymentId(null);
    }
  };

  const handleOpenOrCreatePaymentLink = async (registration) => {
    if (!isRazorpayLinkAllowed(registration)) {
      toast.error(getRazorpayLinkIssue(registration));
      return;
    }

    const existingUrl = registration?.payment_link_url;
    if (existingUrl) {
      window.open(existingUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!selectedEventForRegistrations) return;

    const amount = Math.round(
      Number(registration?.amount_paid || selectedEventForRegistrations?.delegate_fee || 0) * 100
    );
    if (!amount) {
      toast.error('Unable to determine the payment amount for this registration.');
      return;
    }

    setCreatingPaymentLinkId(registration.registration_id);
    try {
      const res = await api.post('/api/payments/event/create-payment-link', {
        member_id: registration.member_id,
        event_id: selectedEventForRegistrations.event_id,
        registration_id: registration.registration_id,
        event_name: selectedEventForRegistrations.name,
        amount,
        currency: selectedEventForRegistrations.currency || 'USD',
        gst_number: registration.gst_number || 'NA',
        company_legal_name: registration.company_legal_name || registration.company_name || 'Company',
        user_name: registration.user_name || registration.member_name || 'Member',
        customer: {
          name: registration.member_name || registration.user_name || 'Member',
          email: registration.member_email,
          contact: registration.member_phone || '',
        },
      });

      const shortUrl = res?.data?.short_url;
      if (!shortUrl) {
        toast.error('Payment link could not be created.');
        return;
      }

      window.open(shortUrl, '_blank', 'noopener,noreferrer');
      toast.success('Payment link created.');
      fetchEventRegistrations(selectedEventForRegistrations.event_id);
    } catch (e) {
      await handleRegistrationActionError(
        e,
        'Failed to create payment link',
        selectedEventForRegistrations.event_id
      );
    } finally {
      setCreatingPaymentLinkId(null);
    }
  };

  const openEditEventDialog = (event) => {
    setEditingEvent(event);
    setEventForm({
      name: event.name,
      description: event.description || '',
      location: event.location || '',
      start_date: event.start_date?.split('T')[0] || '',
      end_date: event.end_date?.split('T')[0] || '',
      currency: event.currency || 'USD',
      early_bird_deadline: event.early_bird_deadline?.split('T')[0] || '',
      early_bird_discount: event.early_bird_discount || 10,
      is_active: event.is_active !== false,
      delegate_fee: event.delegate_fee ?? 0,
      max_attendees: event.max_attendees ?? 100
    });
    setShowEventDialog(true);
  };

  const openEditSponsorshipDialog = (type) => {
    setEditingSponsorshipType(type);
    setSponsorshipForm({
      name: type.name,
      description: type.description || '',
      price: type.price || 0,
      max_quantity: type.max_quantity || 1,
      benefits: type.benefits || [],
      sort_order: type.sort_order || 0,
      is_active: type.is_active !== false
    });
    setShowSponsorshipDialog(true);
  };

  const openEditDelegateDialog = (type) => {
    setEditingDelegateType(type);
    setDelegateForm({
      name: type.name,
      description: type.description || '',
      price: type.price || 0,
      early_bird_price: type.early_bird_price || 0,
      member_price: type.member_price || 0,
      group_discount_threshold: type.group_discount_threshold || 5,
      group_discount_percent: type.group_discount_percent || 10,
      includes: type.includes || [],
      sort_order: type.sort_order || 0,
      is_active: type.is_active !== false
    });
    setShowDelegateDialog(true);
  };

  const getSponsorshipIcon = (name) => {
    const nameLower = name?.toLowerCase() || '';
    if (nameLower.includes('diamond') || nameLower.includes('title')) return <Star className="text-purple-500" />;
    if (nameLower.includes('platinum')) return <Award className="text-blue-500" />;
    if (nameLower.includes('gold')) return <Award className="text-amber-500" />;
    if (nameLower.includes('silver')) return <Award className="text-gray-500" />;
    if (nameLower.includes('bronze')) return <Award className="text-orange-600" />;
    if (nameLower.includes('booth') || nameLower.includes('exhibition')) return <Building2 className="text-green-500" />;
    if (nameLower.includes('dinner') || nameLower.includes('lunch') || nameLower.includes('networking')) return <Utensils className="text-red-500" />;
    if (nameLower.includes('speaker') || nameLower.includes('session')) return <Mic className="text-indigo-500" />;
    if (nameLower.includes('lanyard') || nameLower.includes('bag') || nameLower.includes('gift')) return <Gift className="text-pink-500" />;
    return <Tag className="text-gray-500" />;
  };

  return (
    <div className="space-y-6" data-testid="admin-event-fees">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Event Fees & Sponsorships</h1>
          <p className="text-gray-500">Manage event pricing, sponsorships, and delegate fees</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="events" data-testid="events-tab">
            <Calendar size={16} className="mr-2" />
            Events ({events.length})
          </TabsTrigger>
          <TabsTrigger value="sponsorships" data-testid="sponsorships-tab">
            <Award size={16} className="mr-2" />
            Sponsorship Types ({sponsorshipTypes.length})
          </TabsTrigger>
          <TabsTrigger value="delegates" data-testid="delegates-tab">
            <Users size={16} className="mr-2" />
            Delegate Fees ({delegateFeeTypes.length})
          </TabsTrigger>
        </TabsList>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => { resetEventForm(); setShowEventDialog(true); }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-event-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Event
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading events...</div>
          ) : events.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No events configured yet</p>
                <Button 
                  onClick={() => setShowEventDialog(true)}
                  variant="outline" 
                  className="mt-4"
                >
                  Create your first event
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <Card key={event.event_id} className={!event.is_active ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Calendar size={24} className="text-blue-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{event.name}</CardTitle>
                          <p className="text-sm text-gray-500">{event.location}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {!event.is_active && <Badge variant="outline">Inactive</Badge>}
                        <Button size="sm" variant="outline" onClick={() => openEditEventDialog(event)}>
                          <Edit2 size={14} className="mr-1" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-600"
                          onClick={() => handleDeleteEvent(event.event_id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Start Date</p>
                        <p className="font-medium">{event.start_date ? new Date(event.start_date).toLocaleDateString() : 'TBD'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">End Date</p>
                        <p className="font-medium">{event.end_date ? new Date(event.end_date).toLocaleDateString() : 'TBD'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Currency</p>
                        <p className="font-medium">{event.currency || 'USD'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Early Bird Discount</p>
                        <p className="font-medium">{event.early_bird_discount || 0}%</p>
                      </div>
                    </div>
                    
                    {/* Event Fees Summary */}
                    {event.fees && event.fees.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium text-gray-700 mb-2">Configured Fees:</p>
                        <div className="flex flex-wrap gap-2">
                          {event.fees.map((fee, idx) => (
                            <Badge key={idx} variant="secondary">
                              {fee.name} - ${fee.price?.toLocaleString()}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-700 border-green-300"
                        onClick={() => openRegistrationsDialog(event)}
                      >
                        <Users size={14} className="mr-1" />
                        Registrations
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedEvent(event);
                          setFeeForm({ ...feeForm, event_id: event.event_id });
                          setShowFeeDialog(true);
                        }}
                      >
                        <Plus size={14} className="mr-1" />
                        Add Fee
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-amber-600 border-amber-300"
                        onClick={() => {
                          setSelectedEvent(event);
                          setShowSponsorshipManager(true);
                        }}
                      >
                        <Award size={14} className="mr-1" />
                        Manage Sponsorships
                        {event.sponsorships?.length > 0 && (
                          <Badge className="ml-2 bg-amber-100 text-amber-700">{event.sponsorships.length}</Badge>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Sponsorships Tab */}
        <TabsContent value="sponsorships" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => { resetSponsorshipForm(); setShowSponsorshipDialog(true); }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-sponsorship-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Sponsorship Type
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : sponsorshipTypes.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Award size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No sponsorship types configured yet</p>
                <Button 
                  onClick={() => setShowSponsorshipDialog(true)}
                  variant="outline" 
                  className="mt-4"
                >
                  Create sponsorship types
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sponsorshipTypes.map((type) => (
                <Card key={type.type_id} className={!type.is_active ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getSponsorshipIcon(type.name)}
                        <CardTitle className="text-lg">{type.name}</CardTitle>
                      </div>
                      {!type.is_active && <Badge variant="outline">Inactive</Badge>}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold">${type.price?.toLocaleString()}</span>
                      {type.max_quantity > 1 && (
                        <span className="text-sm text-gray-500 ml-2">(max {type.max_quantity})</span>
                      )}
                    </div>
                    
                    {type.description && (
                      <p className="text-sm text-gray-600">{type.description}</p>
                    )}
                    
                    {type.benefits && type.benefits.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase">Benefits:</p>
                        {type.benefits.slice(0, 3).map((benefit, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <Check size={14} className="mr-2 text-green-500" />
                            {benefit}
                          </div>
                        ))}
                        {type.benefits.length > 3 && (
                          <p className="text-xs text-gray-500">+{type.benefits.length - 3} more</p>
                        )}
                      </div>
                    )}
                    
                    <div className="flex space-x-2 pt-2 border-t">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditSponsorshipDialog(type)}>
                        <Edit2 size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600"
                        onClick={() => handleDeleteSponsorshipType(type.type_id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Delegate Fees Tab */}
        <TabsContent value="delegates" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => { resetDelegateForm(); setShowDelegateDialog(true); }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-delegate-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Delegate Fee Type
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : delegateFeeTypes.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Ticket size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No delegate fee types configured yet</p>
                <Button 
                  onClick={() => setShowDelegateDialog(true)}
                  variant="outline" 
                  className="mt-4"
                >
                  Create delegate fee types
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {delegateFeeTypes.map((type) => (
                <Card key={type.type_id} className={!type.is_active ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Ticket size={20} className="text-blue-500" />
                        <CardTitle className="text-lg">{type.name}</CardTitle>
                      </div>
                      {!type.is_active && <Badge variant="outline">Inactive</Badge>}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-1">
                      <div className="flex items-baseline">
                        <span className="text-3xl font-bold">${type.price?.toLocaleString()}</span>
                        <span className="text-sm text-gray-500 ml-1">/person</span>
                      </div>
                      {type.early_bird_price > 0 && type.early_bird_price < type.price && (
                        <p className="text-sm text-green-600">
                          Early Bird: ${type.early_bird_price?.toLocaleString()}
                        </p>
                      )}
                      {type.member_price > 0 && type.member_price < type.price && (
                        <p className="text-sm text-blue-600">
                          Member Price: ${type.member_price?.toLocaleString()}
                        </p>
                      )}
                    </div>
                    
                    {type.description && (
                      <p className="text-sm text-gray-600">{type.description}</p>
                    )}
                    
                    {/* Group Discount */}
                    {type.group_discount_threshold > 0 && (
                      <div className="p-2 bg-amber-50 rounded-lg text-sm">
                        <p className="text-amber-700">
                          {type.group_discount_percent}% off for {type.group_discount_threshold}+ delegates
                        </p>
                      </div>
                    )}
                    
                    {type.includes && type.includes.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase">Includes:</p>
                        {type.includes.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <Check size={14} className="mr-2 text-green-500" />
                            {item}
                          </div>
                        ))}
                        {type.includes.length > 3 && (
                          <p className="text-xs text-gray-500">+{type.includes.length - 3} more</p>
                        )}
                      </div>
                    )}
                    
                    <div className="flex space-x-2 pt-2 border-t">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditDelegateDialog(type)}>
                        <Edit2 size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600"
                        onClick={() => handleDeleteDelegateType(type.type_id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="w-[min(88vw,64rem)] max-w-[64rem] max-h-[90vh] flex flex-col overflow-hidden p-6 gap-0">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto px-1 pr-4 pt-4 [scrollbar-gutter:stable]">
            <div>
              <Label>Event Name *</Label>
              <Input
                value={eventForm.name}
                onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                placeholder="e.g., OGA Annual Conference 2026"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Brief description of the event..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Location</Label>
              <Input
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                placeholder="e.g., Singapore Marina Bay Sands"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Currency</Label>
                <select
                  value={eventForm.currency}
                  onChange={(e) => setEventForm({ ...eventForm, currency: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="SGD">SGD</option>
                  <option value="AED">AED</option>
                </select>
              </div>
              <div>
                <Label>Early Bird Deadline</Label>
                <Input
                  type="date"
                  value={eventForm.early_bird_deadline}
                  onChange={(e) => setEventForm({ ...eventForm, early_bird_deadline: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Early Bird Discount (%)</Label>
                <Input
                  type="number"
                  value={eventForm.early_bird_discount}
                  onChange={(e) => setEventForm({ ...eventForm, early_bird_discount: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Delegate Fee (member portal)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={eventForm.delegate_fee ?? 0}
                  onChange={(e) => setEventForm({ ...eventForm, delegate_fee: parseFloat(e.target.value) || 0 })}
                  placeholder="e.g., 199"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Max Attendees</Label>
                <Input
                  type="number"
                  min={1}
                  value={eventForm.max_attendees ?? 100}
                  onChange={(e) => setEventForm({ ...eventForm, max_attendees: parseInt(e.target.value) || 100 })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={eventForm.is_active}
                onChange={(e) => setEventForm({ ...eventForm, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <span>Active (visible to members)</span>
            </label>
            
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEventDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveEvent} className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingEvent ? 'Update Event' : 'Create Event'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sponsorship Type Dialog */}
      <Dialog open={showSponsorshipDialog} onOpenChange={setShowSponsorshipDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSponsorshipType ? 'Edit Sponsorship Type' : 'Create Sponsorship Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={sponsorshipForm.name}
                  onChange={(e) => setSponsorshipForm({ ...sponsorshipForm, name: e.target.value })}
                  placeholder="e.g., Diamond Sponsor"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Price ($) *</Label>
                <Input
                  type="number"
                  value={sponsorshipForm.price}
                  onChange={(e) => setSponsorshipForm({ ...sponsorshipForm, price: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={sponsorshipForm.description}
                onChange={(e) => setSponsorshipForm({ ...sponsorshipForm, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Quantity Available</Label>
                <Input
                  type="number"
                  value={sponsorshipForm.max_quantity}
                  onChange={(e) => setSponsorshipForm({ ...sponsorshipForm, max_quantity: parseInt(e.target.value) || 1 })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">How many of this sponsorship can be sold</p>
              </div>
              <div>
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={sponsorshipForm.sort_order}
                  onChange={(e) => setSponsorshipForm({ ...sponsorshipForm, sort_order: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Benefits</Label>
              <div className="flex space-x-2 mt-1">
                <Input
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  placeholder="Add a benefit..."
                  onKeyPress={(e) => e.key === 'Enter' && addBenefit()}
                />
                <Button type="button" onClick={addBenefit} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {sponsorshipForm.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">{benefit}</span>
                    <button onClick={() => removeBenefit(idx)} className="text-red-500 hover:text-red-700">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sponsorshipForm.is_active}
                onChange={(e) => setSponsorshipForm({ ...sponsorshipForm, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <span>Active</span>
            </label>
            
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowSponsorshipDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveSponsorshipType} className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingSponsorshipType ? 'Update Type' : 'Create Type'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delegate Fee Type Dialog */}
      <Dialog open={showDelegateDialog} onOpenChange={setShowDelegateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDelegateType ? 'Edit Delegate Fee Type' : 'Create Delegate Fee Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={delegateForm.name}
                  onChange={(e) => setDelegateForm({ ...delegateForm, name: e.target.value })}
                  placeholder="e.g., Full Conference Pass"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Standard Price ($) *</Label>
                <Input
                  type="number"
                  value={delegateForm.price}
                  onChange={(e) => setDelegateForm({ ...delegateForm, price: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={delegateForm.description}
                onChange={(e) => setDelegateForm({ ...delegateForm, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Early Bird Price ($)</Label>
                <Input
                  type="number"
                  value={delegateForm.early_bird_price}
                  onChange={(e) => setDelegateForm({ ...delegateForm, early_bird_price: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Member Price ($)</Label>
                <Input
                  type="number"
                  value={delegateForm.member_price}
                  onChange={(e) => setDelegateForm({ ...delegateForm, member_price: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="p-4 bg-amber-50 rounded-lg">
              <p className="text-sm font-medium text-amber-800 mb-2">Group Discount</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-amber-700">Min Delegates for Discount</Label>
                  <Input
                    type="number"
                    value={delegateForm.group_discount_threshold}
                    onChange={(e) => setDelegateForm({ ...delegateForm, group_discount_threshold: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-amber-700">Discount Percentage (%)</Label>
                  <Input
                    type="number"
                    value={delegateForm.group_discount_percent}
                    onChange={(e) => setDelegateForm({ ...delegateForm, group_discount_percent: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label>What's Included</Label>
              <div className="flex space-x-2 mt-1">
                <Input
                  value={newInclude}
                  onChange={(e) => setNewInclude(e.target.value)}
                  placeholder="e.g., Access to all sessions..."
                  onKeyPress={(e) => e.key === 'Enter' && addInclude()}
                />
                <Button type="button" onClick={addInclude} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {delegateForm.includes.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">{item}</span>
                    <button onClick={() => removeInclude(idx)} className="text-red-500 hover:text-red-700">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={delegateForm.sort_order}
                  onChange={(e) => setDelegateForm({ ...delegateForm, sort_order: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={delegateForm.is_active}
                    onChange={(e) => setDelegateForm({ ...delegateForm, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDelegateDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveDelegateType} className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingDelegateType ? 'Update Type' : 'Create Type'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Fee to Event Dialog */}
      <Dialog open={showFeeDialog} onOpenChange={setShowFeeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Fee to Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedEvent && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="font-medium">{selectedEvent.name}</p>
                <p className="text-sm text-gray-600">{selectedEvent.location}</p>
              </div>
            )}
            
            <div>
              <Label>Fee Type *</Label>
              <select
                value={feeForm.fee_type}
                onChange={(e) => setFeeForm({ ...feeForm, fee_type: e.target.value, type_id: '' })}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="sponsorship">Sponsorship</option>
                <option value="delegate">Delegate Fee</option>
              </select>
            </div>
            
            <div>
              <Label>{feeForm.fee_type === 'sponsorship' ? 'Sponsorship Type' : 'Delegate Fee Type'} *</Label>
              {feeForm.fee_type === 'delegate' && (
                <p className="text-xs text-gray-500 mt-0.5 mb-1">
                  Delegate fee types are pricing options (e.g. Standard, Early Bird) defined in the Delegate Fees tab. Select one to attach that rate to this event.
                </p>
              )}
              <select
                value={feeForm.type_id}
                onChange={(e) => setFeeForm({ ...feeForm, type_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="">Select type...</option>
                {feeForm.fee_type === 'sponsorship' ? (
                  sponsorshipTypes.filter(t => t.is_active !== false).map(type => (
                    <option key={type.type_id} value={type.type_id}>
                      {type.name} - ${type.price?.toLocaleString()}
                    </option>
                  ))
                ) : (
                  delegateFeeTypes.filter(t => t.is_active !== false).map(type => (
                    <option key={type.type_id} value={type.type_id}>
                      {type.name} - ${type.price?.toLocaleString()}
                    </option>
                  ))
                )}
              </select>
              {feeForm.fee_type === 'delegate' && delegateFeeTypes.filter(t => t.is_active !== false).length === 0 && (
                <p className="text-sm text-amber-600 mt-2">
                  No delegate fee types yet. Create one in the <strong>Delegate Fees</strong> tab above, or run the database seed to add default options (Standard Delegate, Early Bird, VIP).
                </p>
              )}
            </div>
            
            <div>
              <Label>Custom Price (optional)</Label>
              <Input
                type="number"
                value={feeForm.custom_price || ''}
                onChange={(e) => setFeeForm({ ...feeForm, custom_price: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="Leave empty to use default price"
                className="mt-1"
              />
            </div>
            
            {feeForm.fee_type === 'sponsorship' && (
              <div>
                <Label>Quantity Available (optional)</Label>
                <Input
                  type="number"
                  value={feeForm.quantity_available || ''}
                  onChange={(e) => setFeeForm({ ...feeForm, quantity_available: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Leave empty to use default"
                  className="mt-1"
                />
              </div>
            )}
            
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowFeeDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleAddFeeToEvent} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!feeForm.type_id}
              >
                Add Fee
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Sponsorship Manager Dialog */}
      <Dialog open={showSponsorshipManager} onOpenChange={setShowSponsorshipManager}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Award size={20} className="mr-2 text-amber-600" />
              Event Sponsorships
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <EventSponsorshipManager 
              event={selectedEvent}
              onClose={() => setShowSponsorshipManager(false)}
              onUpdate={() => fetchData()}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Event Registrations Dialog */}
      <Dialog open={showRegistrationsDialog} onOpenChange={setShowRegistrationsDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Users size={20} className="mr-2 text-green-600" />
              Registrations – {selectedEventForRegistrations?.name || 'Event'}
            </DialogTitle>
          </DialogHeader>
          {eventRegistrationsLoading ? (
            <div className="py-8 text-center text-gray-500">Loading registrations...</div>
          ) : eventRegistrations.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No registrations yet.</div>
          ) : (
            <div className="space-y-3">
              {eventRegistrations.map((reg) => (
                <div
                  key={reg.registration_id}
                  className="flex items-start justify-between gap-4 p-3 rounded-lg border bg-gray-50"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">Member: {reg.member_name || reg.member_id}</p>
                    <p className="text-sm text-gray-500">Company: {reg.company_name || reg.company_id}</p>
                    <p className="text-xs text-gray-400">
                      {reg.payment_method || '—'} · {reg.registered_at ? new Date(reg.registered_at).toLocaleString() : '—'}
                    </p>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      <div>
                        <span className="text-gray-500">Payment ID:</span>{' '}
                        <span className="font-mono text-gray-900">{reg.payment_id || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Payment Record Status:</span>{' '}
                        <span className="text-gray-900">{reg.payment_record_status || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Amount:</span>{' '}
                        <span className="text-gray-900">
                          {getRegistrationCurrency(reg) === 'INR'
                            ? `₹${Number(reg.amount_paid || 0).toLocaleString()}`
                            : `$${Number(reg.amount_paid || 0).toLocaleString()}`}{' '}
                          {getRegistrationCurrency(reg)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Passes:</span>{' '}
                        <span className="text-gray-900">{reg.total_passes || 1}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Invoice ID:</span>{' '}
                        <span className="font-mono text-gray-900">{reg.invoice_id || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Member Email:</span>{' '}
                        <span className="text-gray-900 break-all">{reg.member_email || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Transaction Ref:</span>{' '}
                        <span className="text-gray-900">{reg.transaction_reference || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Transfer Date:</span>{' '}
                        <span className="text-gray-900">{reg.transfer_date || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Payer:</span>{' '}
                        <span className="text-gray-900">{reg.payer_account_name || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Bank:</span>{' '}
                        <span className="text-gray-900">
                          {reg.payer_bank_name || '—'}
                          {reg.payer_account_last4 ? ` • ****${reg.payer_account_last4}` : ''}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">GST Number:</span>{' '}
                        <span className="text-gray-900">{reg.gst_number || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Company Legal Name:</span>{' '}
                        <span className="text-gray-900">{reg.company_legal_name || '—'}</span>
                      </div>
                    </div>
                    {Array.isArray(reg.selected_fees) && reg.selected_fees.length > 0 && (
                      <div className="mt-3 rounded-md border bg-white p-2">
                        <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wide mb-2">
                          Fee Breakdown
                        </p>
                        <div className="space-y-1.5">
                          {reg.selected_fees.map((fee, index) => (
                            <div
                              key={`${fee.fee_id || fee.name}-${index}`}
                              className="flex items-center justify-between text-xs"
                            >
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900">{fee.name || 'Fee'}</p>
                                <p className="text-gray-500">Qty {fee.quantity || 0}</p>
                              </div>
                              <p className="font-medium text-gray-900">
                                {getRegistrationCurrency(reg) === 'INR'
                                  ? `₹${Number(fee.line_total || 0).toLocaleString()}`
                                  : `$${Number(fee.line_total || 0).toLocaleString()}`}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {getPaymentProofUrl(reg) && (
                      <p className="mt-1 text-xs text-green-700">Bank transfer proof attached.</p>
                    )}
                    {isRazorpayRegistration(reg) && (
                      <p className="mt-1 text-xs text-amber-700">
                        {getRazorpayLinkIssue(reg)}
                      </p>
                    )}
                    {isBankTransferRegistration(reg) && !getPaymentProofUrl(reg) && reg.payment_status === 'Pending' && (
                      <p className="mt-1 text-xs text-red-600">No bank transfer proof is attached yet.</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Badge
                      className={
                        reg.payment_status === 'Completed'
                          ? 'bg-green-100 text-green-700'
                          : reg.payment_status === 'Rejected'
                            ? 'bg-red-100 text-red-700'
                          : 'bg-blue-100 text-blue-700'
                      }
                    >
                      {reg.payment_status}
                    </Badge>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {getPaymentProofUrl(reg) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(getPaymentProofUrl(reg), '_blank', 'noopener,noreferrer')}
                        >
                          <Eye size={14} className="mr-1" />
                          View Proof
                        </Button>
                      )}
                      {isRazorpayRegistration(reg) && isRazorpayLinkAllowed(reg) && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={creatingPaymentLinkId === reg.registration_id}
                          onClick={() => handleOpenOrCreatePaymentLink(reg)}
                        >
                          <Eye size={14} className="mr-1" />
                          {creatingPaymentLinkId === reg.registration_id
                            ? 'Creating...'
                            : reg.payment_link_url
                              ? 'Open Payment Link'
                              : 'Add Payment Link'}
                        </Button>
                      )}
                      {reg.payment_status === 'Pending' && selectedEventForRegistrations && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            disabled={completingPaymentId === reg.registration_id}
                            onClick={() =>
                              handleCompletePayment(
                                selectedEventForRegistrations.event_id,
                                reg.registration_id
                              )
                            }
                          >
                            {completingPaymentId === reg.registration_id ? '...' : 'Complete Payment'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            disabled={rejectingPaymentId === reg.registration_id}
                            onClick={() =>
                              handleRejectPayment(
                                selectedEventForRegistrations.event_id,
                                reg.registration_id
                              )
                            }
                          >
                            <X size={14} className="mr-1" />
                            {rejectingPaymentId === reg.registration_id ? 'Rejecting...' : 'Reject Payment'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEventFees;
